import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import DataSheet from "react-datasheet";
import { Spinner, SkeletonLoader } from "@via/components/loading/loading.tsx";
import { GenericError } from "@via/components/error.tsx";
import { ERROR } from "@via/utils/constants.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import "react-datasheet/lib/react-datasheet.css";
import "./spreadsheet-editor.css";
import { NOTIFICATION } from "@via/context/notifications-context";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import SingleLineDataEditor from "@via/components/spreadsheet-editor/single-line-data-editor.tsx";
import MultiLineDataEditor from "@via/components/spreadsheet-editor/multi-line-data-editor.tsx";
import ColumnSelectorButton from "@via/components/column-selector-button/column-selector-button";

// Cell type definition for react-datasheet
export type Cell = {
  value: string | null;
  options?: string[];
  readOnly?: boolean;
  dataEditor?: any;
};

export type SpreadsheetData = Cell[][];

export enum SpreadsheetActionType {
  PasteCollectionNames = "pasteCollectionNames",
  RenameColumnHeaders = "renameColumnHeaders",
  InsertRowAbove = "insertRowAbove",
  InsertRowBelow = "insertRowBelow",
  ClearRow = "clearRow",
  ClearColumn = "clearColumn",
  DeleteRow = "deleteRow",
  InsertColumnLeft = "insertColumnLeft",
  InsertColumnRight = "insertColumnRight",
  DeleteColumn = "deleteColumn",
  Custom = "custom",
}

type SpreadsheetContextMenuItemPayload = {
  fillValue?: string;
};

export type SpreadsheetContextMenuItem = {
  label: React.ReactNode;
  type: SpreadsheetActionType;
  onClick?: (props?: any) => Promise<string[]>;
  children?: SpreadsheetContextMenuItem[];
  payload?: SpreadsheetContextMenuItemPayload;
};

interface SpreadsheetEditorProps {
  label?: string;
  data?: SpreadsheetData;
  columnLabels?: string[];
  isLoading?: boolean;
  isError?: boolean;
  extraContextMenuItems?: SpreadsheetContextMenuItem[];
  onChange?: (
    data: SpreadsheetData,
    rowIndex?: number,
    rowIndices?: number[]
  ) => void;
  disableContextMenu?: boolean;
  multiLine?: boolean;
  columnVisibility?: Record<string, boolean>;
  onColumnVisibilityChange?: (visibility: Record<string, boolean>) => void;
}

export const SpreadsheetEditor: React.FC<SpreadsheetEditorProps> = ({
  label,
  data = [],
  columnLabels = [],
  isLoading = false,
  isError = false,
  extraContextMenuItems = [],
  onChange,
  disableContextMenu = false,
  multiLine = false,
  columnVisibility,
  onColumnVisibilityChange,
}) => {
  const { setNotification } = useNotifications();
  const [spreadsheetData, setSpreadsheetData] = useState<SpreadsheetData>(data);
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    row: null as number | null,
    col: null as number | null,
  });
  const [isContextMenuItemLoading, setIsContextMenuItemLoading] =
    useState(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollPositionRef = useRef({ x: 0, y: 0 });
  const dataSheetRef = useRef<DataSheet<Cell, string> | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);
  const [openSubmenuPath, setOpenSubmenuPath] = useState<string | null>(null);

  // Toggle column visibility handler
  const toggleColumnVisibility = (columnLabel: string) => {
    if (!onColumnVisibilityChange || !columnVisibility) return;

    onColumnVisibilityChange({
      ...columnVisibility,
      [columnLabel]: !columnVisibility[columnLabel],
    });
  };

  // Sync spreadsheetData with updated data prop
  useEffect(() => {
    setSpreadsheetData(data);
  }, [data]);

  // Reset column widths when column structure changes
  useEffect(() => {
    if (columnWidths.length > 0) {
      setColumnWidths([]);
    }
  }, [columnLabels.length]); // Only when number of columns changes

  // Lock column widths after initial render
  useEffect(() => {
    if (
      tableRef.current &&
      columnLabels.length > 0 &&
      columnWidths.length === 0
    ) {
      // Wait for table to render with content
      requestAnimationFrame(() => {
        const table = tableRef.current;
        if (!table) return;

        // Get all cells from first data row (after header)
        const firstRow = table.querySelector("tbody tr");
        if (!firstRow) return;

        const cells = firstRow.querySelectorAll("td, th");
        const widths: number[] = [];

        cells.forEach(cell => {
          widths.push((cell as HTMLElement).offsetWidth);
        });

        setColumnWidths(widths);
      });
    }
  }, [columnLabels, columnWidths.length]);

  const memoizedSpreadsheetData = useMemo(
    () =>
      spreadsheetData.map(row =>
        row.map(cell => {
          if (cell.dataEditor) {
            return cell; // Avoid recreating cell if dataEditor already exists
          }
          return {
            ...cell,
            dataEditor: multiLine ? MultiLineDataEditor : SingleLineDataEditor,
          };
        })
      ),
    [spreadsheetData, multiLine]
  );

  const getContainerFrom = (el: HTMLElement) =>
    el.closest(".spreadsheet-scrollable-container") as HTMLElement | null;

  const updateSpreadsheetData = (
    newData: SpreadsheetData,
    rowIndex?: number,
    rowIndices?: number[]
  ) => {
    // Capture scroll position immediately before state update
    if (scrollContainerRef.current) {
      scrollPositionRef.current = {
        x: scrollContainerRef.current.scrollLeft,
        y: scrollContainerRef.current.scrollTop,
      };
    }
    setSpreadsheetData(newData);
    if (onChange) {
      onChange(newData, rowIndex, rowIndices);
    }
  };

  const updateContextMenuItemLoading = (loading: boolean) => {
    if (scrollContainerRef.current) {
      scrollPositionRef.current = {
        x: scrollContainerRef.current.scrollLeft,
        y: scrollContainerRef.current.scrollTop,
      };
    }
    setIsContextMenuItemLoading(loading);
  };

  const getFixedContainingBlock = (el: HTMLElement): HTMLElement | null => {
    let n: HTMLElement | null = el.parentElement as HTMLElement | null;
    while (n) {
      const cs = getComputedStyle(n);
      const createsCB =
        cs.transform !== "none" ||
        cs.perspective !== "none" ||
        cs.filter !== "none" ||
        ["paint", "layout", "style", "strict"].includes(cs.contain) ||
        cs.willChange.includes("transform") ||
        cs.willChange.includes("perspective") ||
        cs.willChange.includes("filter");
      if (createsCB) return n;
      n = n.parentElement as HTMLElement | null;
    }
    return null; // then it's the viewport
  };

  // Handle right-click to show context menu
  const handleContextMenu = (e: React.MouseEvent, row: number, col: number) => {
    if (disableContextMenu) {
      return; // Do nothing if context menu is disabled
    }
    e.preventDefault();

    const container = getContainerFrom(e.currentTarget as HTMLElement);
    if (!container) return;

    // Capture scroll position
    if (scrollContainerRef.current) {
      scrollPositionRef.current = {
        x: scrollContainerRef.current.scrollLeft,
        y: scrollContainerRef.current.scrollTop,
      };
    }

    // fixed containing block (may differ per sheet)
    const cb = getFixedContainingBlock(container);
    const cbRect = cb ? cb.getBoundingClientRect() : { left: 0, top: 0 };

    // Use viewport coords, adjusted by the fixed containing block's rect
    const fx = e.clientX - cbRect.left;
    const fy = e.clientY - cbRect.top;

    // Check if the right-click target is the cell editor input
    const isEditorInput = (e.target as HTMLElement).classList.contains(
      "data-editor"
    );
    // TODO: Temporarlily disabled right-click on editor input. Implement a better way to handle right-click on the editor input to avoid losing data when right-clicking on it
    if (isEditorInput) {
      return;
    }

    setContextMenu({
      visible: true,
      x: fx,
      y: fy,
      row,
      col,
    });
  };

  // useLayoutEffect to restore scroll position synchronously after DOM updates
  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = scrollPositionRef.current.x;
      scrollContainerRef.current.scrollTop = scrollPositionRef.current.y;
    }
  }, [spreadsheetData, isContextMenuItemLoading, contextMenu.visible]);

  // Helper function to create a new row with appropriate options
  const createNewRowWithOptions = (templateRow: Cell[]): Cell[] =>
    templateRow.map(cell => ({
      value: "",
      options: cell.options ? [...cell.options] : undefined, // Preserve options if present
    }));

  // Helper function to clear a row
  const clearRow = (rowIndex: number): SpreadsheetData =>
    spreadsheetData.map((row, idx) =>
      idx === rowIndex ? row.map(cell => ({ ...cell, value: "" })) : row
    );

  // Helper function to clear a column
  const clearColumn = (colIndex: number): SpreadsheetData =>
    spreadsheetData.map(row =>
      row.map((cell, idx) => (idx === colIndex ? { ...cell, value: "" } : cell))
    );

  const handleSpreadsheetAction = (action: SpreadsheetActionType) => {
    if (contextMenu.row === null || contextMenu.col === null) return;

    let updatedData = [...spreadsheetData];

    switch (action) {
      case SpreadsheetActionType.InsertRowAbove:
        updatedData = [
          ...updatedData.slice(0, contextMenu.row),
          createNewRowWithOptions(spreadsheetData[contextMenu.row]),
          ...updatedData.slice(contextMenu.row),
        ];
        break;
      case SpreadsheetActionType.InsertRowBelow:
        updatedData = [
          ...updatedData.slice(0, contextMenu.row + 1),
          createNewRowWithOptions(spreadsheetData[contextMenu.row]),
          ...updatedData.slice(contextMenu.row + 1),
        ];
        break;
      case SpreadsheetActionType.ClearRow:
        updatedData = clearRow(contextMenu.row);
        break;
      case SpreadsheetActionType.ClearColumn:
        updatedData = clearColumn(contextMenu.col);
        break;
      case SpreadsheetActionType.DeleteRow:
        // If it's the last row, clear the values instead of deleting the row
        updatedData =
          spreadsheetData.length === 1
            ? clearRow(contextMenu.row)
            : updatedData.filter((_, index) => index !== contextMenu.row);
        break;
      default:
        break;
    }
    updateSpreadsheetData(
      updatedData,
      contextMenu.row,
      action === SpreadsheetActionType.ClearColumn
        ? updatedData.map((_, index) => index)
        : [contextMenu.row]
    );
    closeContextMenu();
  };

  const handleDeleteColumn = async (
    item: SpreadsheetContextMenuItem
  ): Promise<void> => {
    if (contextMenu.col === null) return;
    if (spreadsheetData?.length > 0) {
      const firstRow = spreadsheetData[0];
      if (firstRow?.length === 1) {
        setNotification({
          id: "delete-column-error",
          message: "Delete Column",
          details: "You cannot delete the last column.",
          variant: NOTIFICATION.ERROR,
        });
        return;
      }
    }
    let updatedData = [...spreadsheetData];
    updatedData = updatedData.map(row =>
      row.filter((_, index) => index !== contextMenu.col)
    );
    updateSpreadsheetData(
      updatedData,
      0,
      updatedData.map((_, index) => index)
    );
    await item.onClick?.(contextMenu.col);
    closeContextMenu();
  };

  const handleInsertColumnLeft = async (
    item: SpreadsheetContextMenuItem
  ): Promise<void> => {
    if (contextMenu.col === null) return;
    await item.onClick?.(contextMenu.col);
    closeContextMenu();
  };

  const handleInsertColumnRight = async (
    item: SpreadsheetContextMenuItem
  ): Promise<void> => {
    if (contextMenu.col === null) return;
    await item.onClick?.(contextMenu.col);
    closeContextMenu();
  };

  const handleRenameColumnHeaders = async (
    item: SpreadsheetContextMenuItem
  ): Promise<void> => {
    if (contextMenu.col === null) return;
    await item.onClick?.(contextMenu.col);
    closeContextMenu();
  };

  const handlePasteCollectionNames = async (
    item: SpreadsheetContextMenuItem
  ): Promise<void> => {
    const { row, col } = contextMenu;
    if (row === null || col === null) return;
    if (!spreadsheetData[row][col].options && !isContextMenuItemLoading) {
      updateContextMenuItemLoading(true);
      const items = (await item.onClick?.()) ?? [];
      // Determine how many additional rows are needed
      const rowsNeeded = Math.max(
        0,
        row + items.length - spreadsheetData.length
      );
      const updatedData = [...spreadsheetData];
      // Insert additional rows if needed
      if (rowsNeeded > 0) {
        const newRowTemplate = updatedData[row].map(cell => ({
          ...cell,
          value: "", // Initialize empty value
        }));
        for (let i = 0; i < rowsNeeded; i++) {
          updatedData.push([...newRowTemplate]);
        }
      }
      // Add items to rows
      items.forEach((item, index) => {
        updatedData[row + index][col] = {
          ...updatedData[row + index][col],
          value: item,
        };
      });
      updateSpreadsheetData(
        updatedData,
        row,
        items.map((_, index) => row + index)
      );
      closeContextMenu();
      updateContextMenuItemLoading(false);
    }
  };

  const shouldDisableExtraItem = (
    item: SpreadsheetContextMenuItem
  ): boolean => {
    const { row, col } = contextMenu;
    if (row === null || col === null) {
      return true;
    }
    const cellHasOptions = !!spreadsheetData[row]?.[col]?.options;
    switch (item.type) {
      case SpreadsheetActionType.InsertColumnLeft:
      case SpreadsheetActionType.InsertColumnRight:
      case SpreadsheetActionType.DeleteColumn:
      case SpreadsheetActionType.RenameColumnHeaders:
        return cellHasOptions;
      case SpreadsheetActionType.PasteCollectionNames:
        return cellHasOptions || isContextMenuItemLoading;
      default:
        return false;
    }
  };

  const handleExtraItemSelection = async (
    item: SpreadsheetContextMenuItem
  ): Promise<void> => {
    if (contextMenu.row === null || contextMenu.col === null) return;
    switch (item.type) {
      case SpreadsheetActionType.InsertColumnLeft:
        await handleInsertColumnLeft(item);
        return;
      case SpreadsheetActionType.InsertColumnRight:
        await handleInsertColumnRight(item);
        return;
      case SpreadsheetActionType.DeleteColumn:
        await handleDeleteColumn(item);
        return;
      case SpreadsheetActionType.RenameColumnHeaders:
        await handleRenameColumnHeaders(item);
        return;
      case SpreadsheetActionType.PasteCollectionNames:
        await handlePasteCollectionNames(item);
        return;
      case SpreadsheetActionType.Custom: {
        const fillValue = item.payload?.fillValue;
        if (typeof fillValue !== "undefined") {
          const updatedData = [...spreadsheetData];
          updatedData[contextMenu.row][contextMenu.col] = {
            ...updatedData[contextMenu.row][contextMenu.col],
            value: fillValue,
          };
          updateSpreadsheetData(updatedData, contextMenu.row, [
            contextMenu.row,
          ]);
        }
        if (item.onClick) {
          await item.onClick({ row: contextMenu.row, col: contextMenu.col });
        }
        closeContextMenu();
        return;
      }
      default:
        if (item.onClick) {
          await item.onClick(contextMenu.col);
        }
        closeContextMenu();
    }
  };

  const renderExtraMenuItems = (
    items: SpreadsheetContextMenuItem[],
    parentPath = "",
    isSubMenu = false
  ): React.ReactNode =>
    items.map((item, index) => {
      const itemPath = parentPath ? `${parentPath}-${index}` : `${index}`;
      const hasChildren = Boolean(item.children?.length);
      const disabled = shouldDisableExtraItem(item);
      const showSubmenu = hasChildren && openSubmenuPath === itemPath;

      return (
        <li
          key={`context-item-${itemPath}`}
          className={classNames(
            "font-semibold p-2 hover:bg-gray-200 whitespace-nowrap",
            hasChildren ? "relative pr-4" : "",
            disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
            item.type === SpreadsheetActionType.PasteCollectionNames ||
              item.type === SpreadsheetActionType.DeleteColumn
              ? "border-b border-gray-300"
              : ""
          )}
          onMouseEnter={() => {
            if (hasChildren) {
              setOpenSubmenuPath(itemPath);
            } else if (!isSubMenu) {
              setOpenSubmenuPath(null);
            }
          }}
          onClick={() => {
            if (hasChildren || disabled) return;
            void handleExtraItemSelection(item);
          }}>
          <div className="flex items-center justify-between gap-2">
            {item.type === SpreadsheetActionType.PasteCollectionNames ? (
              <Spinner loading={isContextMenuItemLoading}>{item.label}</Spinner>
            ) : (
              item.label
            )}
            {hasChildren && <span className="text-gray-400">&rsaquo;</span>}
          </div>
          {hasChildren && showSubmenu && (
            <ul
              className="absolute top-0 left-full ml-2 bg-white border rounded shadow-md z-20"
              style={{ maxHeight: "220px", overflowY: "auto" }}>
              {renderExtraMenuItems(item.children!, itemPath, true)}
            </ul>
          )}
        </li>
      );
    });

  const closeContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0, row: null, col: null });
    setOpenSubmenuPath(null);
  };

  // Custom paste parser to handle pasting single value into multiple cells
  const handleParsePaste = (str: string): string[][] => {
    // Parse the clipboard data
    const rows = str.split(/\r\n|\n|\r/);
    const data = rows.map(row => row.split("\t"));

    // Check if single value is being pasted (1x1)
    if (data.length === 1 && data[0].length === 1 && dataSheetRef.current) {
      // Get the current selection from DataSheet's state
      // Note: The library's type definitions use CellReference {row, col} but at runtime
      // the state actually uses Location {i, j}. This is a bug in react-datasheet types.
      const state = dataSheetRef.current.state as DataSheet.DataSheetState & {
        start?: DataSheet.Location;
        end?: DataSheet.Location;
      };

      if (state?.start && state?.end) {
        const { start, end } = state;

        // Calculate selection dimensions
        const rowCount = Math.abs(end.i - start.i) + 1;
        const colCount = Math.abs(end.j - start.j) + 1;

        // If multiple cells are selected, expand the single value to fill the selection
        if (rowCount > 1 || colCount > 1) {
          const singleValue = data[0][0];
          const expandedData: string[][] = [];

          for (let i = 0; i < rowCount; i++) {
            const row: string[] = [];
            for (let j = 0; j < colCount; j++) {
              row.push(singleValue);
            }
            expandedData.push(row);
          }

          return expandedData;
        }
      }
    }

    return data;
  };

  // Render the spreadsheet with table format
  const renderSheet = ({ className, children }): React.ReactNode => (
    <div
      ref={scrollContainerRef}
      className="overflow-x-auto overflow-y-auto relative spreadsheet-scrollable-container">
      <table
        ref={tableRef}
        className={classNames("border-collapse", className)}>
        <thead className="sticky top-0 bg-gray-100 z-10">
          <tr>
            <th className="cell spreadsheet-table-header-left"></th>
            {columnLabels.map((label, index) => {
              // Skip hidden columns
              if (columnVisibility && columnVisibility[label] === false) {
                return null;
              }
              return (
                <th
                  key={label}
                  className="cell spreadsheet-table-header"
                  style={
                    columnWidths.length > index + 1 &&
                    columnWidths[index + 1] !== undefined
                      ? { width: `${columnWidths[index + 1]}px` }
                      : undefined
                  }>
                  {label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {children.map((row: React.ReactElement, rowIndex: number) =>
            React.cloneElement(row, {
              children: [
                <th
                  key={`row-number-${rowIndex}`}
                  className="cell spreadsheet-table-header-left">
                  {rowIndex + 1}
                </th>,
                ...React.Children.toArray(row.props.children),
              ],
            })
          )}
        </tbody>
      </table>
    </div>
  );
  // Custom cell renderer
  const customCellRenderer = ({
    row,
    col,
    className,
    onDoubleClick,
    onKeyUp,
    onMouseDown,
    onMouseOver,
    children,
  }: DataSheet.CellRendererProps<Cell, string>): React.ReactNode => {
    // Skip hidden columns
    const columnLabel = columnLabels[col];
    if (columnVisibility && columnVisibility[columnLabel] === false) {
      return null;
    }

    // Access options from the current cell
    const cell = spreadsheetData[row][col];
    const options = cell?.options;

    // Get width for this column
    // col is 0-indexed data column (0 = first data column)
    // columnWidths[0] = row number column (not used here)
    // columnWidths[col + 1] = width for data column at index col
    const cellStyle =
      columnWidths.length > col + 1 && columnWidths[col + 1] !== undefined
        ? { width: `${columnWidths[col + 1]}px` }
        : undefined;

    if (options) {
      // Render a select dropdown if options exist
      return (
        <td
          className={classNames("spreadsheet-cell", className)}
          style={cellStyle}
          onKeyUp={onKeyUp}
          onMouseOver={onMouseOver}
          onContextMenu={e => handleContextMenu(e, row, col)}>
          <select
            value={cell?.value || ""}
            onChange={e => {
              const updatedData = [...spreadsheetData];
              updatedData[row][col] = {
                ...cell,
                value: e.target.value,
              };
              updateSpreadsheetData(updatedData, row, [row]);
            }}
            className="w-full p-0 border border-gray-300 rounded">
            <option value="" disabled hidden>
              Select an option
            </option>
            {options.map((option: string) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </td>
      );
    }

    // Default rendering if no options exist
    return (
      <td
        className={classNames("spreadsheet-cell", className)}
        style={cellStyle}
        onDoubleClick={onDoubleClick}
        onKeyUp={onKeyUp}
        onMouseDown={onMouseDown}
        onMouseOver={onMouseOver}
        onContextMenu={e => handleContextMenu(e, row, col)}>
        {children}
      </td>
    );
  };

  if (isLoading) {
    return (
      <div className="py-6">
        <SkeletonLoader count={1} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="via-generic-error lg:py-12">
        <GenericError message={ERROR} />
      </div>
    );
  }

  return (
    <div
      className={classNames(
        "relative spreadsheet-editor-container",
        multiLine ? "multi-line-mode" : "single-line-mode"
      )}>
      {label && <label className="text-sm font-medium">{label}</label>}

      {/* Column Visibility Toggle */}
      {columnVisibility && onColumnVisibilityChange && (
        <div className="flex justify-end mb-2">
          <ColumnSelectorButton
            columns={columnLabels.map(label => ({ id: label, label }))}
            columnVisibility={columnVisibility}
            onToggle={columnId => {
              toggleColumnVisibility(columnId);
            }}
          />
        </div>
      )}

      <div className="mt-2">
        <DataSheet
          ref={dataSheetRef}
          data={memoizedSpreadsheetData}
          valueRenderer={cell => cell.value}
          sheetRenderer={renderSheet}
          cellRenderer={customCellRenderer}
          parsePaste={handleParsePaste}
          onCellsChanged={changes => {
            const updatedData = [...spreadsheetData];
            changes.forEach(({ row, col, value }) => {
              updatedData[row][col] = { ...updatedData[row][col], value };
            });
            const changedRows = Array.from(
              new Set(changes.map(change => change.row))
            ).sort((a, b) => a - b);
            updateSpreadsheetData(
              updatedData,
              changedRows[0],
              changedRows.length > 0 ? changedRows : undefined
            );
          }}
        />
      </div>

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          className="fixed bg-white whitespace-nowrap border rounded shadow-md p-2 z-10"
          style={{ top: contextMenu.y, left: contextMenu.x }}>
          <ul>
            {renderExtraMenuItems(extraContextMenuItems)}
            <li
              className="cursor-pointer p-2 hover:bg-gray-200"
              onClick={() =>
                handleSpreadsheetAction(SpreadsheetActionType.InsertRowAbove)
              }>
              Insert row above
            </li>
            <li
              className="cursor-pointer p-2 hover:bg-gray-200"
              onClick={() =>
                handleSpreadsheetAction(SpreadsheetActionType.InsertRowBelow)
              }>
              Insert row below
            </li>
            <li
              className="cursor-pointer p-2 hover:bg-gray-200 text-red-500"
              onClick={() =>
                handleSpreadsheetAction(SpreadsheetActionType.ClearRow)
              }>
              Clear row
            </li>
            <li
              className="cursor-pointer p-2 hover:bg-gray-200 text-red-500"
              onClick={() =>
                handleSpreadsheetAction(SpreadsheetActionType.ClearColumn)
              }>
              Clear column
            </li>
            <li
              className="cursor-pointer p-2 hover:bg-gray-200 text-red-500"
              onClick={() =>
                handleSpreadsheetAction(SpreadsheetActionType.DeleteRow)
              }>
              Delete row
            </li>
          </ul>
        </div>
      )}

      {/* Hide the context menu when clicking outside */}
      {contextMenu.visible && (
        <div className="fixed inset-0 z-0" onClick={closeContextMenu} />
      )}
    </div>
  );
};
